import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const OpsOrgLoginAttempts = () => {
  const { orgId } = useParams();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    return {
      start_date: toDateInputValue(start),
      end_date: toDateInputValue(end),
      email: '',
      status: '',
      method: '',
      portal: '',
      ip: '',
    };
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [total, setTotal] = useState(0);

  const loadAttempts = async (nextFilters = filters, nextPage = page, nextPageSize = pageSize) => {
    if (!orgId) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/login-attempts`, {
        params: { ...nextFilters, page: nextPage, page_size: nextPageSize },
      });
      setAttempts(response.data?.attempts || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load login attempts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttempts(filters, page, pageSize);
  }, [orgId]);

  const handleChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleApply = (event) => {
    event.preventDefault();
    setPage(1);
    loadAttempts(filters, 1, pageSize);
  };

  const handleReset = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    const next = {
      start_date: toDateInputValue(start),
      end_date: toDateInputValue(end),
      email: '',
      status: '',
      method: '',
      portal: '',
      ip: '',
    };
    setFilters(next);
    setPage(1);
    loadAttempts(next, 1, pageSize);
  };

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  const handlePageChange = (nextPage) => {
    const clamped = Math.min(Math.max(nextPage, 1), totalPages);
    setPage(clamped);
    loadAttempts(filters, clamped, pageSize);
  };

  const handleExport = async () => {
    if (!orgId) return;
    try {
      const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/login-attempts`, {
        params: { ...filters, page, page_size: pageSize, export: 'csv' },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'org-login-attempts.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to export login attempts.');
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Login Attempts</h2>
          <div className="text-muted">Review sign-in activity for this organization.</div>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Filters</h5>
          <form className="row g-3 align-items-start" onSubmit={handleApply}>
            <div className="col-12 col-md-4">
              <label className="form-label">Email</label>
              <input
                type="text"
                className="form-control"
                value={filters.email}
                onChange={handleChange('email')}
                placeholder="Email address"
              />
              <div className="form-text">Enter at least 5 characters.</div>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.start_date}
                onChange={handleChange('start_date')}
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.end_date}
                onChange={handleChange('end_date')}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Status</label>
              <select className="form-select" value={filters.status} onChange={handleChange('status')}>
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Method</label>
              <select className="form-select" value={filters.method} onChange={handleChange('method')}>
                <option value="">All</option>
                <option value="password">Email &amp; Password</option>
                <option value="otp">Email One-Time Code</option>
                <option value="google">Google</option>
                <option value="microsoft">Microsoft</option>
                <option value="oidc">Custom OIDC</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Portal</label>
              <select className="form-select" value={filters.portal} onChange={handleChange('portal')}>
                <option value="">All</option>
                <option value="account">Account</option>
                <option value="ops">Ops</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">IP Address</label>
              <input
                type="text"
                className="form-control"
                value={filters.ip}
                onChange={handleChange('ip')}
                placeholder="IP address"
              />
            </div>
            <div className="col-12 d-flex gap-2">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                Apply Filters
              </button>
              <button type="button" className="btn btn-outline-primary" onClick={handleReset} disabled={loading}>
                Reset
              </button>
              <button type="button" className="btn btn-outline-secondary ms-auto" onClick={handleExport}>
                Export CSV
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Login Attempts</h5>
          {loading ? (
            <div className="text-muted">Loading login attempts...</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Date/Time</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Method</th>
                    <th>Portal</th>
                    <th>IP</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.map((row) => (
                    <tr key={row.id}>
                      <td>{new Date(row.attempted_at).toLocaleString()}</td>
                      <td>{row.email}</td>
                      <td>{row.success ? 'Success' : 'Failed'}</td>
                      <td>{row.method}</td>
                      <td>{row.portal || '-'}</td>
                      <td>{row.ip_address || '-'}</td>
                      <td>{row.reason || '-'}</td>
                    </tr>
                  ))}
                  {attempts.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-muted text-center">No login attempts found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted small">
              {total === 0 ? 'Showing 0 of 0' : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
            </div>
            <div className="btn-group">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpsOrgLoginAttempts;
